// theme/js/core-ui/megamenu.js

import { sectionLogger } from "./_logger";

const DEBUG = true;
const logger = sectionLogger("MegaMenu", DEBUG);

class MegaMenu extends HTMLElement {
  constructor() {
    super();

    this.timer = null;
    this.isOpen = false;
    this.activePanel = null;
  }

  connectedCallback() {
    this.triggers = this.querySelectorAll("[data-megamenu-trigger]");
    this.panels = this.querySelectorAll("[data-megamenu-panel]");
    this.layer = this.querySelector("#megamenu-layer");

    if (!this.triggers.length || !this.panels.length || !this.layer) {
      logger.warn("missing DOM hooks");
      return;
    }

    this.interaction = this.dataset.megamenuInteraction || "hover";
    logger.info("interaction", this.interaction);

    this.init();
  }

  init() {
    this.triggers.forEach((trigger) => {
      const name = trigger.dataset.megamenuTrigger;

      // HOVER MODE
      if (this.interaction === "hover") {
        trigger.addEventListener("mouseenter", () => {
          clearTimeout(this.timer);
          this.open(name, trigger);
        });

        trigger.addEventListener("mouseleave", () => {
          this.closeWithDelay();
        });
      }

      // CLICK MODE
      if (this.interaction === "click") {
        trigger.addEventListener("click", (e) => {
          e.preventDefault();

          const panel = this.querySelector(`[data-megamenu-panel="${name}"]`);

          const alreadyOpen = panel && !panel.classList.contains("hidden");

          if (alreadyOpen) {
            this.close();
          } else {
            this.open(name, trigger);
          }
        });
      }
    });

    // Hover buffer (empêche fermeture en passant au panel)
    if (this.interaction === "hover") {
      this.layer.addEventListener("mouseenter", () => {
        clearTimeout(this.timer);
      });

      this.layer.addEventListener("mouseleave", () => {
        this.closeWithDelay();
      });
    }

    // Click outside
    document.addEventListener("click", (e) => {
      if (!this.isOpen) return;

      const clickedOnTrigger = e.target.closest("[data-megamenu-trigger]");
      const clickedInsideLayer = e.target.closest("#megamenu-layer");

      const insideThisMenu =
        this.contains(clickedOnTrigger) || this.contains(clickedInsideLayer);

      if (!insideThisMenu) {
        this.close();
      }
    });

    // ESC
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.close();
    });

    // Scroll close (viewport)
    let lastScrollY = window.scrollY;

    window.addEventListener(
      "scroll",
      () => {
        if (!this.isOpen) return;

        const delta = Math.abs(window.scrollY - lastScrollY);
        if (delta > 6) {
          this.close();
        }

        lastScrollY = window.scrollY;
      },
      { passive: true },
    );

    // Resize simple (comme avant)
    window.addEventListener("resize", () => {
      if (window.innerWidth < 1024) {
        this.close();
        this.activePanel = null;
      }
    });

    this.initSubmenus();
  }

  // ─────────────────────────
  // SUBMENUS
  // ─────────────────────────

  initSubmenus() {
    const subTriggers = this.querySelectorAll("[data-megamenu-subtrigger]");
    if (!subTriggers.length) return;

    subTriggers.forEach((trigger) => {
      const key = trigger.dataset.megamenuSubtrigger;
      const parentPanel = trigger.closest("[data-megamenu-panel]");
      if (!parentPanel) return;

      const categoriesList = parentPanel.querySelector(
        "[data-megamenu-categories]",
      );

      const activate = () => {
        if (this.activePanel !== parentPanel) return;

        this.showSubpanel(parentPanel, key);

        if (categoriesList) {
          categoriesList.querySelectorAll("li").forEach((li) => {
            li.classList.remove("is-active");
          });
        }

        const li = trigger.closest("li");
        if (li) li.classList.add("is-active");
      };

      trigger.addEventListener("mouseenter", activate);

      // 👇 NOUVEAU
      trigger.addEventListener("mouseleave", () => {
        if (!categoriesList) return;

        categoriesList.querySelectorAll("li").forEach((li) => {
          li.classList.remove("is-active");
        });
      });

      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        activate();
      });
    });
  }

  // ─────────────────────────
  // OPEN / CLOSE
  // ─────────────────────────

  open(name, triggerEl) {
    this.panels.forEach((p) => p.classList.add("hidden"));
    this.triggers.forEach((t) => t.setAttribute("aria-expanded", "false"));

    const panel = this.querySelector(`[data-megamenu-panel="${name}"]`);
    if (!panel) return;

    panel.classList.remove("hidden");

    this.isOpen = true;
    this.activePanel = panel;

    if (triggerEl) triggerEl.setAttribute("aria-expanded", "true");

    this.resetCategories(panel);
  }

  closeWithDelay() {
    this.timer = setTimeout(() => this.close(), 150);
  }

  close() {
    this.panels.forEach((p) => p.classList.add("hidden"));
    this.triggers.forEach((t) => t.setAttribute("aria-expanded", "false"));

    this.isOpen = false;
    this.activePanel = null;
  }

  resetCategories(panel) {
    const subTriggers = panel.querySelectorAll("[data-megamenu-subtrigger]");
    const subPanels = panel.querySelectorAll("[data-megamenu-subpanel]");

    if (!subTriggers.length || !subPanels.length) return;

    // Cache tous les subpanels
    subPanels.forEach((p) => p.classList.add("hidden"));

    const categoriesList = panel.querySelector("[data-megamenu-categories]");

    if (!categoriesList) return;

    // Reset is-active
    categoriesList.querySelectorAll("li").forEach((li) => {
      li.classList.remove("is-active");
    });

    // 👉 Active la première catégorie
    const firstTrigger = subTriggers[0];
    if (!firstTrigger) return;

    const key = firstTrigger.dataset.megamenuSubtrigger;

    const firstLi = firstTrigger.closest("li");
    if (firstLi) firstLi.classList.add("is-active");

    const firstPanel = panel.querySelector(`[data-megamenu-subpanel="${key}"]`);

    if (firstPanel) firstPanel.classList.remove("hidden");
  }

  showSubpanel(panel, key) {
    const subPanels = panel.querySelectorAll("[data-megamenu-subpanel]");
    subPanels.forEach((p) => p.classList.add("hidden"));

    const target = panel.querySelector(`[data-megamenu-subpanel="${key}"]`);
    if (target) target.classList.remove("hidden");
  }
}
if (!customElements.get("mega-menu")) {
  customElements.define("mega-menu", MegaMenu);
}
